import {
  CloudLightning,
  Hotel,
  Mail,
  MapPin,
  MessageSquareText,
  Timer,
  Trophy,
  Users,
} from 'lucide-react';

const PROBLEMS = [
  {
    icon: CloudLightning,
    problem: 'Lightning closes a diamond',
    fix: 'Close the field, slide the games, and send one SMS and email to the teams on that diamond.',
  },
  {
    icon: Timer,
    problem: 'Scores never get posted',
    fix: 'The field board flags the overdue game. The coach posts the result from the dugout, and standings update.',
  },
  {
    icon: MessageSquareText,
    problem: 'The weekend lives in a group text',
    fix: 'The Messages station is one SMS and email dispatcher for coaches and staff, with a delivery record for that tournament.',
  },
] as const;

const COACH_TOOLS = [
  { icon: MapPin, label: 'Today’s games, with diamond and time' },
  { icon: Timer, label: 'Post the score' },
  { icon: Trophy, label: 'Live standings' },
  { icon: Users, label: 'Matchup analysis' },
  { icon: Hotel, label: 'The official hotel link' },
  { icon: Mail, label: 'Staff contacts, so the director can reach the bench' },
] as const;

const MESSAGE_LINES = [
  'SMS and email from one place.',
  'A message aimed at the teams on a diamond.',
  'A log of what went out.',
] as const;

export function FieldStories() {
  return (
    <section className="bg-[#020617] py-16 md:py-24" data-testid="field-stories">
      <div className="container mx-auto px-4">
        <div className="mx-auto mb-10 max-w-3xl text-center">
          <p className="font-broadcast text-xs uppercase tracking-[0.22em] text-emerald-300">
            Saturday, from the desk
          </p>
          <h2 className="mt-3 font-broadcast text-3xl uppercase tracking-wide text-white md:text-5xl">
            The problems the weekend actually brings
          </h2>
        </div>

        <div className="mx-auto grid max-w-6xl grid-cols-1 gap-4 md:grid-cols-2">
          {PROBLEMS.map(item => {
            const Icon = item.icon;
            return (
              <article
                key={item.problem}
                className={`rounded-2xl border border-white/10 bg-white/5 p-5 md:p-6 ${
                  item.problem === 'The weekend lives in a group text' ? 'md:col-span-2' : ''
                }`}
              >
                <div className="mb-4 flex h-11 w-11 items-center justify-center rounded-lg bg-emerald-500/15">
                  <Icon className="h-5 w-5 text-emerald-300" aria-hidden />
                </div>
                <h3 className="font-broadcast text-lg uppercase tracking-wide text-white md:text-xl">
                  {item.problem}
                </h3>
                <p className="mt-3 text-sm leading-relaxed text-white/75 md:text-base">
                  {item.fix}
                </p>
              </article>
            );
          })}
        </div>

        <div className="mx-auto mt-8 grid max-w-6xl grid-cols-1 gap-4 lg:grid-cols-2 lg:items-stretch">
          <article className="rounded-2xl border border-white/10 bg-white/5 p-5 md:p-6">
            <p className="font-broadcast text-xs uppercase tracking-[0.22em] text-championship-gold">
              Coach Command Centre
            </p>
            <h3 className="mt-2 font-broadcast text-2xl uppercase tracking-wide text-white">
              One link at the diamond
            </h3>
            <div className="mx-auto mt-6 max-w-sm rounded-[2rem] border border-white/15 bg-[#020617] p-4 shadow-[0_20px_50px_rgba(0,0,0,0.35)]">
              <div className="mx-auto mb-4 h-1.5 w-16 rounded-full bg-white/20" aria-hidden />
              <ul className="space-y-2">
                {COACH_TOOLS.map(tool => {
                  const Icon = tool.icon;
                  return (
                    <li
                      key={tool.label}
                      className="flex min-h-11 items-center gap-3 rounded-xl border border-white/10 px-3 text-sm text-white"
                    >
                      <Icon className="h-4 w-4 shrink-0 text-emerald-300" aria-hidden />
                      <span>{tool.label}</span>
                    </li>
                  );
                })}
              </ul>
            </div>
            <p className="mt-4 text-sm text-white/70">Bookmark one link for the diamond.</p>
          </article>

          <article className="flex flex-col rounded-2xl border border-white/10 bg-white/5 p-5 md:p-6">
            <p className="font-broadcast text-xs uppercase tracking-[0.22em] text-emerald-300">
              Messages station
            </p>
            <h3 className="mt-2 font-broadcast text-2xl uppercase tracking-wide text-white">
              SMS and email, from one desk
            </h3>
            <ul className="mt-6 flex flex-1 flex-col justify-center gap-3">
              {MESSAGE_LINES.map(line => (
                <li
                  key={line}
                  className="flex min-h-11 items-center gap-3 rounded-xl border border-white/10 bg-[#020617] px-4 text-sm text-white md:text-base"
                >
                  <Mail className="h-4 w-4 shrink-0 text-emerald-300" aria-hidden />
                  <span>{line}</span>
                </li>
              ))}
            </ul>
          </article>
        </div>
      </div>
    </section>
  );
}
